"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import AnimateOnScroll from "./AnimateOnScroll";
// import { useCounselorTeamMembers } from "@/hooks/useCounselorTeamMembers";
import { teamMembers } from "@/lib/teamMembers";
import { SITE_URL, ORGANIZATION_NAME } from "@/lib/metadata";

export type TeamMember = (typeof teamMembers)[number];

const teamSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Our Team",
  itemListElement: teamMembers.map((member, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "Person",
      name: member.name,
      jobTitle: member.title,
      description: member.description,
      image: new URL(member.image, SITE_URL).href,
      url: new URL(`/teams/${member.slug.toLowerCase()}`, SITE_URL).href,
      worksFor: {
        "@type": "Organization",
        name: ORGANIZATION_NAME,
        url: SITE_URL,
      },
    },
  })),
};

export const TeamMemberCard = ({ member }: { member: TeamMember }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isMobile = window.matchMedia("(max-width: 767px)").matches;
        if (isMobile && entry.isIntersecting) {
          video.play().catch(() => {});
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  const handleMouseEnter = () => {
    if (!window.matchMedia("(max-width: 767px)").matches) {
      videoRef.current?.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    if (!window.matchMedia("(max-width: 767px)").matches) {
      const video = videoRef.current;
      if (!video) return;
      video.pause();
      video.currentTime = 0;
    }
  };

  return (
    <Link
      href={`/teams/${member.slug.toLowerCase()}`}
      className="block h-full w-full max-w-[492px]"
    >
      <article
        className="lg::h-[500px] group relative mx-auto h-[400px] w-full max-w-[492px] overflow-hidden rounded-[14px] border border-[#006DFF]/20 bg-slate-900 shadow-[inset_0_0_36px_rgba(255,255,255,0.6)] sm:h-[450px] sm:rounded-[16px] md:rounded-[18px] lg:rounded-[20px] 3xl:h-[548px]"
        aria-label={`Team member: ${member.name}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <video
          ref={videoRef}
          src={member.video}
          poster={member.image}
          muted
          playsInline
          preload="metadata"
          className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />

        <div
          className="absolute inset-0 z-10"
          style={{
            background:
              member.theme === "yellow"
                ? "linear-gradient(0deg, #A67C00 0%, rgba(166, 124, 0, 0.00) 50%)"
                : member.theme === "red"
                  ? "linear-gradient(0deg, #BB0205 0%, rgba(187, 2, 5, 0.00) 50%)"
                  : member.theme === "purple"
                    ? "linear-gradient(0deg, #5B21B6 0%, rgba(91, 33, 182, 0.00) 50%)"
                    : "linear-gradient(0deg, #0251BB 0%, rgba(2, 81, 187, 0.00) 50%)",
          }}
        />

        <div className="absolute bottom-0 left-0 right-0 z-20 flex flex-col gap-2 p-4 sm:gap-2.5 sm:p-5 md:gap-3 md:p-6 lg:gap-[13.5px] lg:p-[27px]">
          <div className="flex flex-col items-start justify-center gap-1 text-center sm:gap-1.5">
            <h3 className="font-crimsonText text-[24px] font-bold leading-tight text-white sm:text-[26px] md:text-[28px] lg:text-[32px]">
              {member.name}
            </h3>
            <span className="font-onest text-[14px] font-semibold leading-tight text-white/90 sm:text-[16px] md:text-[18px] lg:text-[20px]">
              {member.title}
            </span>
          </div>

          <p className="text-left font-onest text-[12px] font-light leading-normal text-white/80 sm:text-[13px] md:text-[14px]">
            {member.description.slice(0, 150)}...
          </p>
        </div>
      </article>
    </Link>
  );
};

const OurTeam = () => {
  // const { teamMembers } = useCounselorTeamMembers();

  return (
    <section className="w-full bg-[#F7FAFF]" aria-labelledby="our-team-heading">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(teamSchema).replace(/</g, "\\u003c"),
        }}
      />
      <div className="container">
        <AnimateOnScroll className="mb-8 flex flex-col items-center gap-2 text-center sm:mb-10 sm:gap-3 md:mb-12 md:gap-4 lg:mb-16">
          <span
            data-from-left
            className="font-crimsonText text-[20px] font-bold leading-none text-[#2857A7] sm:text-[24px] md:text-[28px]"
          >
            Our Team
          </span>
          <h2 id="our-team-heading" className="heading text-v3-secondary">
            The People Behind Your Care
          </h2>
        </AnimateOnScroll>

        <div className="flex flex-col items-center justify-center gap-5 md:flex-row">
          {teamMembers.map((member) => (
            <TeamMemberCard key={member.slug} member={member} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default OurTeam;
